import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { List, ListItem } from '@fluentui/react-list-preview';
import { Card, ToggleButton, mergeClasses, Skeleton, SkeletonItem, Caption2, Caption1Strong } from '@fluentui/react-components';
import { useTemplateImageStyles, useTemplateGridStyles } from './style';

export const TemplateImageCard = forwardRef(({
  isActive,
  templateGroupId,
  templateAttributes,
  setClickedTemplate,
  image,
  i18n,
}, imageCardRef) => {
  const classes = useTemplateImageStyles();
  const itemRef = useRef(null);

  const getAssetCountInfo = () => {
    const { logoCount, imageCount, textAreaCount } = templateAttributes.assetCounts;
    const result = [];
    if (logoCount && logoCount > 1) result.push(i18n.getString(_TL_('{{logoCount}} logos'), { logoCount }));
    else if (logoCount && logoCount > 0) result.push(i18n.getString(_TL_('{{logoCount}} logo'), { logoCount }));
    if (imageCount && imageCount > 1) result.push(i18n.getString(_TL_('{{imageCount}} images'), { imageCount }));
    else if (imageCount && imageCount > 0) result.push(i18n.getString(_TL_('{{imageCount}} image'), { imageCount }));
    if (textAreaCount && textAreaCount > 1) result.push(i18n.getString(_TL_('{{textAreaCount}} texts'), { textAreaCount }));
    else if (textAreaCount && textAreaCount > 0) result.push(i18n.getString(_TL_('{{textAreaCount}} text'), { textAreaCount }));
    return result.join('・');
  };

  const onClick = () => {
    setClickedTemplate(templateGroupId);
  };

  useImperativeHandle(imageCardRef, () => ({
    focus() {
      const node = itemRef.current;
      if (node && isActive) node.focus();
    },
  }), [isActive]);

  return (
    <ListItem ref={itemRef} onAction={onClick} className={mergeClasses(classes.flexColumn, classes.templateWithCaption)}>
      <Card className={classes.imageCard} aria-hidden>
        <img src={image} alt={i18n.getString(_TL_('Preview of display ad template'))} />
      </Card>
      <div className={mergeClasses(classes.flexColumn, classes.caption)}>
        <Caption1Strong className={classes.darkGrayText}> {templateAttributes.templateGroupName} </Caption1Strong>
        <Caption2 className={classes.grayText}> {getAssetCountInfo()} </Caption2>
      </div>
    </ListItem>
  );
});

TemplateImageCard.propTypes = {
  isActive: PropTypes.bool.isRequired,
  templateGroupId: PropTypes.string.isRequired,
  templateAttributes: PropTypes.shape({
    templateGroupName: PropTypes.string,
    tag: PropTypes.string,
    assetCounts: PropTypes.shape({}),
  }).isRequired,
  setClickedTemplate: PropTypes.func.isRequired,
  image: PropTypes.string.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
};

export const TemplateGrid = forwardRef(({
  templates,
  selectedTemplateId,
  templateTagOptions,
  templateAttributesMap,
  setClickedTemplate,
  loading,
  i18n,
}, templateCardRef) => {
  const classes = useTemplateGridStyles();
  const [selectedFilter, setSelectedFilter] = useState(templateTagOptions.all);
  const filterOptions = [templateTagOptions.all, templateTagOptions.brandAwareness, templateTagOptions.performance];

  return (
    <>
      <div className={mergeClasses('template-filter', classes.filtersRow)}>
        {filterOptions.map((filter, index) => (
          <React.Fragment key={filter}>
            <ToggleButton
              onClick={() => setSelectedFilter(filter)}
              className={mergeClasses(`filter-${index}`, classes.filterButton)}
              disabled={loading}
              checked={selectedFilter === filter}
            >
              {i18n.getString(filter)}
            </ToggleButton>
          </React.Fragment>
        ))}
      </div>
      {loading ? (
        <div aria-busy="true" aria-label={i18n.getString(_TL_('Loading templates'))}>
          <Skeleton>
            <div className={classes.skeletonContainer}>
              <SkeletonItem size={128} />
              <SkeletonItem size={128} />
              <SkeletonItem size={128} />
              <SkeletonItem size={128} />
              <SkeletonItem size={128} />
            </div>
          </Skeleton>
        </div>
      ) : (
        <List
          navigationMode="items"
          className={mergeClasses('template-grid', classes.gridContainer)}
        >
          {templates.map(template => (
            (selectedFilter === templateTagOptions.all ||
              templateAttributesMap[template.templateGroupId].tag === selectedFilter
            ) && (
            <TemplateImageCard
              ref={template.templateGroupId === selectedTemplateId ? templateCardRef : null}
              key={`${template.templateGroupId}`}
              image={template.preview}
              isActive={template.templateGroupId === selectedTemplateId}
              templateGroupId={template.templateGroupId}
              templateAttributes={templateAttributesMap[template.templateGroupId]}
              setClickedTemplate={setClickedTemplate}
              i18n={i18n}
            />
            )
          ))}
        </List>
      )}
    </>
  );
});

TemplateGrid.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  templates: PropTypes.arrayOf(PropTypes.shape({
    templateGroupId: PropTypes.string.isRequired,
    preview: PropTypes.string.isRequired,
  })),
  templateTagOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  templateAttributesMap: PropTypes.objectOf(PropTypes.shape({
    templateGroupName: PropTypes.string,
    tag: PropTypes.string,
    assetCounts: PropTypes.shape({}),
  })).isRequired,
  setClickedTemplate: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  selectedTemplateId: PropTypes.string,
};

TemplateGrid.defaultProps = {
  templates: [],
  loading: true,
  selectedTemplateId: null,
};
